import { forwardRef, useImperativeHandle, useState } from "react";
import {
  Box,
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { TEMPLATE_SECTION_BODY_PLACEHOLDER } from "@/utils/constants";
import { appShowNotification } from "@/utils/utils";
import {
  createTemplateSection,
  createTemplateTextbox,
} from "@/graphql/mutations";
import {
  CreateTemplateSectionInput,
  CreateTemplateSectionMutation,
  CreateTemplateSectionMutationVariables,
  CreateTemplateTextboxInput,
  CreateTemplateTextboxMutation,
  CreateTemplateTextboxMutationVariables,
} from "@/API";
import { databaseMigrationAdapter } from "@/services/DatabaseMigrationAdapter";

const client = generateClient();

interface SectionData {
  title: string;
  body: string;
}

interface NewSectionsFormProps {
  handleModalCancel: () => void;
  template?: { id?: string };
  fetchTemplate?: () => void;
  isEditMode: boolean;
}

const NewSectionsForm = forwardRef(
  (
    {
      handleModalCancel,
      template,
      isEditMode,
      fetchTemplate,
    }: NewSectionsFormProps,
    ref,
  ) => {
    const [loading, setLoading] = useState(false);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [savingSections, setSavingSections] = useState<number[]>([]);
    const [sections, setSections] = useState<SectionData[]>([
      { title: "", body: "" },
    ]);

    const resetForm = () => {
      setSections([{ title: "", body: "" }]);
      setCurrentSectionIndex(0);
      setSavingSections([]);
    };

    useImperativeHandle(ref, () => ({
      resetForm,
    }));

    const handleSaveAllSections = async () => {
      if (
        sections.some(
          (section) => !section.title.trim() || !section.body.trim(),
        )
      ) {
        appShowNotification({
          message: "Please provide section's title and body",
          title: "Warning",
          variant: "warning",
        });
        return;
      }
      try {
        setLoading(true);
        const validSections = sections.filter(
          (section, index) =>
            section.title.trim() !== "" &&
            section.body.trim() !== "" &&
            !savingSections.includes(index),
        );

        for (let i = 0; i < validSections.length; i++) {
          const sectionIndex = sections.findIndex(
            (s) =>
              s.title === validSections[i].title &&
              s.body === validSections[i].body,
          );
          if (sectionIndex !== -1 && !savingSections.includes(sectionIndex)) {
            await handleSaveTemplateSection(sectionIndex);
          }
        }
        fetchTemplate?.();
        setLoading(false);
        handleModalCancel();
      } catch (error) {
        console.error("error creating template textbox batch:", error);
      } finally {
        setLoading(false);
      }
    };

    const handleSectionTitleChange = (text: string) => {
      const newSections = [...sections];
      newSections[currentSectionIndex].title = text;
      setSections(newSections);
    };

    const handleSectionBodyChange = (text: string) => {
      const newSections = [...sections];
      newSections[currentSectionIndex].body = text;
      setSections(newSections);
    };

    const isCurrentSectionValid = () => {
      return (
        sections[currentSectionIndex]?.title?.trim() !== "" &&
        sections[currentSectionIndex]?.body?.trim() !== ""
      );
    };

    const handleAddSection = () => {
      if (isCurrentSectionValid()) {
        setSections([...sections, { title: "", body: "" }]);
        setCurrentSectionIndex(sections.length);
      }
    };

    const handleDeleteSection = (index: number) => {
      if (sections.length <= 1) {
        appShowNotification({
          message: "At least one section is required.",
          title: "Warning",
          variant: "warning",
        });
        return;
      }

      const newSections = sections.filter((_, i) => i !== index);
      setSections(newSections);

      if (currentSectionIndex >= newSections.length) {
        setCurrentSectionIndex(newSections.length - 1);
      } else if (currentSectionIndex === index) {
        setCurrentSectionIndex(
          Math.max(0, currentSectionIndex - (index === 0 ? 0 : 1)),
        );
      }
    };

    const handleSaveTemplateSection = async (sectionIndex: number) => {
      if (!template?.id) {
        return;
      }
      setSavingSections((prev) => [...prev, sectionIndex]);

      try {
        const createSectionInput: CreateTemplateSectionInput = {
          name: sections[sectionIndex].title,
          isSimple: true,
          tID: template.id,
        };
        const createSectionVars: CreateTemplateSectionMutationVariables = {
          input: createSectionInput,
        };

        const createSectionResult = await client.graphql<
          GraphQLQuery<CreateTemplateSectionMutation>
        >({
          query: createTemplateSection,
          variables: createSectionVars,
        });
        try {
          const createTextboxInput: CreateTemplateTextboxInput = {
            value: sections[sectionIndex].body,
            sID: createSectionResult?.data?.createTemplateSection?.id!,
            tID: template.id,
          };

          const createTextboxVars: CreateTemplateTextboxMutationVariables = {
            input: createTextboxInput,
          };
          await client.graphql<GraphQLQuery<CreateTemplateTextboxMutation>>({
            query: createTemplateTextbox,
            variables: createTextboxVars,
          });

          // Dual-write: send to Supabase as template section
          await databaseMigrationAdapter.handleTemplateSectionOperation('CREATE', {
            template_id: template.id!,
            name: sections[sectionIndex].title,
            content: sections[sectionIndex].body,
            order_index: sectionIndex,
          })
        } catch (err) {
          console.error("error creating template textbox:", err);
        }
      } catch (err) {
        console.error("error creating template section:", err);
      } finally {
        setSavingSections((prev) => prev.filter((i) => i !== sectionIndex));
      }
    };

    const handleNavigateToSection = (index: number) => {
      if (index >= 0 && index < sections.length) {
        setCurrentSectionIndex(index);
      }
    };

    const renderSectionNavigator = () => {
      return (
        <Group gap="xs" mb="md">
          <Text fw={500}>Sections:</Text>
          <ScrollArea type="auto" style={{ width: "100%" }}>
            <Group gap="xs">
              {sections.map((section, index) => (
                <Box p={0} key={index} pos={"relative"} mr={10}>
                  <Button
                    variant={
                      currentSectionIndex === index ? "filled" : "subtle"
                    }
                    radius={20}
                    loading={savingSections.includes(index)}
                    bg={
                      currentSectionIndex === index
                        ? undefined
                        : "var(--mantine-color-gray-light)"
                    }
                    color={currentSectionIndex === index ? undefined : "black"}
                    onClick={() => handleNavigateToSection(index)}
                  >
                    {section.title || `Section ${index + 1}`}
                  </Button>
                  <Box
                    onClick={() => handleDeleteSection(index)}
                    pos={"absolute"}
                    right={-15}
                    top={0}
                    p={0}
                    w={24}
                    h={24}
                    variant="white"
                    bg={"red"}
                    style={{
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IconX size={14} color="white" />
                  </Box>
                </Box>
              ))}
            </Group>
          </ScrollArea>
        </Group>
      );
    };

    return (
      <Stack gap={0}>
        <Text fw={600} mb="sm">
          {isEditMode
            ? "Add more sections to your template"
            : "Create sections for your template"}
        </Text>
        <Text size="sm" mb="md">
          {isEditMode
            ? "Customize your template by creating additional sections."
            : "Customize your template by creating different sections."}
        </Text>
        {sections.length > 1 && renderSectionNavigator()}

        <TextInput
          label="Section Title"
          placeholder="Example: Objective"
          description="What would you like to name this section?"
          value={sections[currentSectionIndex].title}
          onChange={(event) =>
            handleSectionTitleChange(event.currentTarget.value)
          }
          required
        />

        <Textarea
          label="Section Body"
          description="Use square brackets [] to indicate variables or inputs. Manta will replace the [brackets]
        with your text in the final report. Please note that you can add more sections anytime!"
          placeholder={TEMPLATE_SECTION_BODY_PLACEHOLDER}
          value={sections[currentSectionIndex].body}
          onChange={(event) =>
            handleSectionBodyChange(event.currentTarget.value)
          }
          required
          minRows={5}
          autosize
          mt={"md"}
        />
        <Group justify="center" mt="md">
          <Button
            leftSection={<IconPlus />}
            onClick={handleAddSection}
            h={40}
            radius={30}
            fw={500}
            disabled={!isCurrentSectionValid()}
          >
            Add another section
          </Button>
          {sections.length > 1 && (
            <Button
              leftSection={<IconTrash />}
              color="red"
              h={40}
              radius={30}
              fw={500}
              onClick={() => handleDeleteSection(currentSectionIndex)}
            >
              Delete this section
            </Button>
          )}
        </Group>
        <Group justify="center" mt="xl">
          <Button
            onClick={handleSaveAllSections}
            fullWidth
            h={40}
            radius={30}
            fw={500}
            disabled={loading}
          >
            {loading ? <Loader size="sm" /> : "Save"}
          </Button>
        </Group>
      </Stack>
    );
  },
);

export default NewSectionsForm;
